import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import CatalogueVisitorAuthModal from "./CatalogueVisitorAuthModal";
import {
  attachVisitorFromCatalogueSession,
  fetchCatalogueVisitorMe,
} from "./catalogueVisitorClient";
import {
  clearCatalogueVisitorSession,
  getCachedCatalogueVisitorProfile,
  getCatalogueVisitorToken,
  setCachedCatalogueVisitorProfile,
  setCatalogueVisitorToken,
  type CatalogueVisitorProfile,
} from "./catalogueVisitorSession";
import { loadPublicCatalogueSession } from "./publicCatalogueHelpers";

export type CatalogueVisitorContextValue = {
  profile: CatalogueVisitorProfile | null;
  /** True when a catalogue visitor JWT is stored for this browser. */
  isVisitorSession: boolean;
  /** Current public catalogue id when browsing a shared catalogue (for registration context). */
  catalogueId?: string | null;
  openAuthModal: () => void;
  closeAuthModal: () => void;
  signOutVisitor: () => void;
  signInVisitor: (token: string, profile: CatalogueVisitorProfile | null) => void;
  refreshVisitorProfile: () => Promise<void>;
  /**
   * After a share-link unlock, attach the visitor JWT from the proven catalogue session.
   * Does not log in or register using a client-supplied phone.
   */
  ensureVisitorForCommerce: (hint?: { sessionToken?: string }) => Promise<boolean>;
};

const CatalogueVisitorContext = createContext<CatalogueVisitorContextValue | null>(null);

export function useCatalogueVisitor(): CatalogueVisitorContextValue {
  const ctx = useContext(CatalogueVisitorContext);
  if (!ctx) {
    return {
      profile: null,
      isVisitorSession: false,
      catalogueId: null,
      openAuthModal: () => {},
      closeAuthModal: () => {},
      signOutVisitor: () => {},
      signInVisitor: () => {},
      refreshVisitorProfile: async () => {},
      ensureVisitorForCommerce: async () => false,
    };
  }
  return ctx;
}

type ProviderProps = {
  children: React.ReactNode;
  /** When set, visitor registration links to this catalogue’s studio org (party / analytics). */
  catalogueId?: string | null;
};

export function CatalogueVisitorProvider({ children, catalogueId }: ProviderProps) {
  const [token, setToken] = useState<string | null>(() => getCatalogueVisitorToken());
  const [profile, setProfile] = useState<CatalogueVisitorProfile | null>(() =>
    getCachedCatalogueVisitorProfile()
  );
  const [authOpen, setAuthOpen] = useState(false);

  const refreshVisitorProfile = useCallback(async () => {
    if (!token) {
      setProfile(null);
      return;
    }
    const json = await fetchCatalogueVisitorMe();
    if (json?.success && json.data && typeof json.data === "object") {
      const d = json.data as Record<string, string>;
      const next: CatalogueVisitorProfile = {
        visitorId: String(d.id || ""),
        name: String(d.name || ""),
        phone: String(d.phone || ""),
        email: d.email != null ? String(d.email) : undefined,
      };
      if (next.visitorId) {
        setProfile(next);
        setCachedCatalogueVisitorProfile(next);
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      void refreshVisitorProfile();
    }
  }, [token, refreshVisitorProfile]);

  const signOutVisitor = useCallback(() => {
    clearCatalogueVisitorSession();
    setToken(null);
    setProfile(null);
  }, []);

  const onSignedIn = useCallback((nextToken: string, p: CatalogueVisitorProfile | null) => {
    const t = nextToken.trim();
    setCatalogueVisitorToken(t);
    setToken(t);
    if (p) {
      setCachedCatalogueVisitorProfile(p);
      setProfile(p);
      return;
    }
    void (async () => {
      const json = await fetchCatalogueVisitorMe();
      if (json?.success && json.data && typeof json.data === "object") {
        const d = json.data as Record<string, string>;
        const next: CatalogueVisitorProfile = {
          visitorId: String(d.id || ""),
          name: String(d.name || ""),
          phone: String(d.phone || ""),
          email: d.email != null ? String(d.email) : undefined,
        };
        if (next.visitorId) {
          setCachedCatalogueVisitorProfile(next);
          setProfile(next);
        }
      }
    })();
  }, []);

  const ensureVisitorForCommerce = useCallback(
    async (hint?: { sessionToken?: string }) => {
      if (getCatalogueVisitorToken()) {
        return true;
      }
      const cid = String(catalogueId || "").trim();
      const linkToken =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("link") || undefined
          : undefined;
      const session =
        String(hint?.sessionToken || "").trim() ||
        loadPublicCatalogueSession(cid, linkToken) ||
        "";
      if (!cid || !session) {
        return false;
      }
      const payload = await attachVisitorFromCatalogueSession(cid, session);
      if (!payload?.token) {
        return false;
      }
      onSignedIn(payload.token, payload.profile);
      return true;
    },
    [catalogueId, onSignedIn]
  );

  const value = useMemo<CatalogueVisitorContextValue>(
    () => ({
      profile,
      isVisitorSession: Boolean(token),
      catalogueId: catalogueId ?? null,
      openAuthModal: () => setAuthOpen(true),
      closeAuthModal: () => setAuthOpen(false),
      signOutVisitor,
      signInVisitor: onSignedIn,
      refreshVisitorProfile,
      ensureVisitorForCommerce,
    }),
    [
      profile,
      token,
      catalogueId,
      signOutVisitor,
      onSignedIn,
      refreshVisitorProfile,
      ensureVisitorForCommerce,
    ]
  );

  return (
    <CatalogueVisitorContext.Provider value={value}>
      {children}
      <CatalogueVisitorAuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onSignedIn={onSignedIn}
        catalogueId={catalogueId ?? null}
      />
    </CatalogueVisitorContext.Provider>
  );
}
